import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { SignalResponse } from 'src/app/features/working-area/interfaces';

/**
 * Append-optimized IndexedDB cache for live-test signal packets.
 *
 * A live test can run for 12+ hours at high sample rates, so the cache is
 * designed as an append-only log rather than a single growing blob: packets
 * are buffered in memory and flushed to IndexedDB in batches (one record per
 * batch). Batching keeps writes infrequent so persistence never slows the UI,
 * while a flush on page unload guarantees the in-flight batch is written too,
 * so a mid-test refresh replays the whole session back into the chart with no
 * gaps.
 *
 * Records are scoped by `roomKey` (`${deviceId}:${testId}`) so tests running
 * in separate tabs never leak into each other.
 */
@Injectable({ providedIn: 'root' })
export class LiveSignalCacheService {
  private readonly DB_NAME = 'LiveSignalCacheDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'signals';
  private readonly ROOM_INDEX = 'roomKey';

  // Flush cadence. Packets are held in memory and written either every
  // FLUSH_INTERVAL_MS or once a room accumulates BATCH_MAX packets, whichever
  // comes first — plus a best-effort flush on unload.
  private readonly FLUSH_INTERVAL_MS = 5000;
  private readonly BATCH_MAX = 25;

  private dbPromise: Promise<IDBDatabase>;
  /** Resolved handle, kept for synchronous use inside the unload handler. */
  private db: IDBDatabase | null = null;

  /** Per-room in-memory buffers awaiting their next batched write. */
  private pending = new Map<string, SignalResponse[]>();
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.dbPromise = this.openDB();
    this.registerUnloadHandlers();
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          // Out-of-line auto-increment keys: the key itself carries insertion
          // order, so an index cursor over `roomKey` yields batches in the
          // exact order they were written.
          const store = db.createObjectStore(this.STORE_NAME, {
            autoIncrement: true,
          });
          store.createIndex(this.ROOM_INDEX, 'roomKey', { unique: false });
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        this.db = db;

        db.onversionchange = () => {
          console.warn('LiveSignalCache: version change detected, closing');
          db.close();
          this.db = null;
          this.dbPromise = this.openDB();
        };

        resolve(db);
      };

      request.onerror = () => reject(request.error);
    });
  }

  private async getDB(): Promise<IDBDatabase> {
    try {
      const db = await this.dbPromise;
      if (!db.objectStoreNames.contains(this.STORE_NAME)) {
        throw new Error('DB invalid');
      }
      return db;
    } catch {
      this.dbPromise = this.openDB();
      return this.dbPromise;
    }
  }

  /**
   * Queue a single (already filtered) signal packet for a room.
   *
   * The packet is buffered in memory and written later as part of a batch, so
   * this stays cheap on the live-plotting path. A shallow clone is stored
   * because the chart mutates the packet after handing it off.
   */
  append(roomKey: string, packet: SignalResponse): void {
    if (!roomKey || !packet) return;

    const buffer = this.pending.get(roomKey) ?? [];
    buffer.push(this.clonePacket(packet));
    this.pending.set(roomKey, buffer);

    this.ensureFlushTimer();

    if (buffer.length >= this.BATCH_MAX) {
      void this.flushRoom(roomKey);
    }
  }

  /** Write a room's buffered packets as a single batch record. */
  async flushRoom(roomKey: string): Promise<void> {
    const packets = this.pending.get(roomKey);
    if (!packets || !packets.length) return;

    // Detach the buffer up-front so packets arriving during the async write
    // accumulate into the next batch rather than being lost or double-written.
    this.pending.set(roomKey, []);

    try {
      const db = await this.getDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(this.STORE_NAME, 'readwrite');
        tx.objectStore(this.STORE_NAME).put({ roomKey, packets });
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
    } catch (err) {
      // Re-queue the failed batch ahead of anything newer so nothing is lost.
      const newer = this.pending.get(roomKey) ?? [];
      this.pending.set(roomKey, [...packets, ...newer]);
      this.captureError(err, { operation: 'flushRoom', roomKey });
    }
  }

  private async flushAll(): Promise<void> {
    await Promise.all(
      [...this.pending.keys()].map((roomKey) => this.flushRoom(roomKey)),
    );
  }

  private ensureFlushTimer(): void {
    if (this.flushTimer != null) return;
    this.flushTimer = setInterval(() => {
      void this.flushAll();
    }, this.FLUSH_INTERVAL_MS);
  }

  /**
   * Best-effort synchronous flush for page unload. IndexedDB writes cannot be
   * awaited during unload, so this fires the transactions synchronously using
   * the already-open handle and lets the browser commit them as it tears down.
   */
  private flushAllSync(): void {
    if (!this.db) return;

    for (const [roomKey, packets] of this.pending.entries()) {
      if (!packets.length) continue;
      this.pending.set(roomKey, []);
      try {
        const tx = this.db.transaction(this.STORE_NAME, 'readwrite');
        tx.objectStore(this.STORE_NAME).put({ roomKey, packets });
      } catch {
        // Nothing more we can do while the page is unloading.
      }
    }
  }

  private registerUnloadHandlers(): void {
    if (typeof window === 'undefined') return;

    // `visibilitychange` → hidden is the most reliable "last chance" callback
    // (it also fires before the page is frozen or put in the back/forward
    // cache); `pagehide` covers the remaining unload cases.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flushAllSync();
    });
    window.addEventListener('pagehide', () => this.flushAllSync());
  }

  /**
   * Load every persisted packet for a room, in the order it was received.
   * Used once, on chart init, to restore session history after a refresh.
   */
  async load(roomKey: string): Promise<SignalResponse[]> {
    if (!roomKey) return [];

    const db = await this.getDB();

    return new Promise<SignalResponse[]>((resolve) => {
      const packets: SignalResponse[] = [];
      const tx = db.transaction(this.STORE_NAME, 'readonly');
      const index = tx.objectStore(this.STORE_NAME).index(this.ROOM_INDEX);
      const req = index.openCursor(IDBKeyRange.only(roomKey));

      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          const value = cursor.value as { packets?: SignalResponse[] };
          if (value?.packets?.length) packets.push(...value.packets);
          cursor.continue();
        } else {
          resolve(packets);
        }
      };

      req.onerror = () => {
        this.captureError(req.error, { operation: 'load', roomKey });
        resolve(packets);
      };
    });
  }

  /**
   * Delete all cached packets for a room. Called when a test ends so finished
   * tests do not accumulate storage.
   */
  async clearRoom(roomKey: string): Promise<void> {
    if (!roomKey) return;

    this.pending.delete(roomKey);
    const db = await this.getDB();

    return new Promise<void>((resolve) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const index = store.index(this.ROOM_INDEX);
      const req = index.openKeyCursor(IDBKeyRange.only(roomKey));

      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        }
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        this.captureError(tx.error, { operation: 'clearRoom', roomKey });
        resolve();
      };
      tx.onabort = () => {
        this.captureError(tx.error, {
          operation: 'clearRoom',
          roomKey,
          stage: 'abort',
        });
        resolve();
      };
    });
  }

  /**
   * Drop every cached room that is not in `activeRoomKeys`.
   *
   * Called when the live-tests screen loads so the cache only ever retains
   * data for tests that are currently running. This is the primary guard
   * against unbounded growth: finished / stale tests never linger, so the DB
   * size is bounded by the set of live tests rather than by history depth.
   */
  async reconcile(activeRoomKeys: string[]): Promise<void> {
    const active = new Set(activeRoomKeys);

    // Drop buffered packets for rooms that are no longer active too.
    for (const roomKey of this.pending.keys()) {
      if (!active.has(roomKey)) this.pending.delete(roomKey);
    }

    const db = await this.getDB();

    return new Promise<void>((resolve) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const req = store.openCursor();

      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        const value = cursor.value as { roomKey?: string };
        if (!value?.roomKey || !active.has(value.roomKey)) {
          cursor.delete();
        }
        cursor.continue();
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        this.captureError(tx.error, { operation: 'reconcile' });
        resolve();
      };
      tx.onabort = () => {
        this.captureError(tx.error, { operation: 'reconcile', stage: 'abort' });
        resolve();
      };
    });
  }

  /**
   * Wipe the entire live-signal cache. Called on logout, alongside the other
   * IndexedDB caches, so no signal data outlives the session.
   */
  async clearAll(): Promise<void> {
    this.pending.clear();
    const db = await this.getDB();

    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      tx.objectStore(this.STORE_NAME).clear();

      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        this.captureError(tx.error, { operation: 'clearAll' });
        reject(tx.error);
      };
      tx.onabort = () => {
        this.captureError(tx.error, { operation: 'clearAll', stage: 'abort' });
        reject(tx.error);
      };
    });
  }

  private clonePacket(packet: SignalResponse): SignalResponse {
    return {
      ...packet,
      data: (packet.data ?? []).map((ch) => ({
        ...ch,
        data: ch.data ? [...ch.data] : ch.data,
      })),
    };
  }

  private captureError(error: any, context: Record<string, any>) {
    Sentry.withScope((scope) => {
      scope.setTag('module', 'live-signal-cache');
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      scope.setExtra('userAgent', navigator.userAgent);
      Sentry.captureException(error);
    });
  }
}
